import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Alert, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { AnimatePresence } from 'framer-motion';
import { AuthCard } from 'src/auth/components/AuthCard';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { ApiError } from 'src/error/ApiError';
import { WizardProgress } from './components/WizardProgress';
import { useSubmitOnboardingSignup } from './hooks/useSubmitOnboardingSignup';
import { applySubmissionError, OnboardingSignupFormValues } from './applySubmissionError';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { FirstNameStep } from './screens/FirstNameStep';
import { CompanyNameStep } from './screens/CompanyNameStep';
import { FrnStep } from './screens/FrnStep';
import { ReviewStep, ReviewEditableField } from './screens/ReviewStep';
import { BookCallIntroStep } from './screens/BookCallIntroStep';
import { BookCallScreen } from './screens/BookCallScreen';
import { ConfirmationScreen } from './screens/ConfirmationScreen';
import Config from 'src/common/Config';

type OnboardingInviteTokenResponse = {
  email: string;
};

// Matches the backend's own validator (`_FRN_PATTERN` in OnboardingSignupModels.py) so a
// malformed FRN is caught client-side with a clear inline message before ever reaching the
// submission endpoint.
const FRN_PATTERN = /^\d{6,7}$/;

type WizardStep = 'firstName' | 'companyName' | 'frn' | 'review';

const STEP_INDEX: Record<WizardStep, number> = {
  firstName: 0,
  companyName: 1,
  frn: 2,
  review: 3,
};
const TOTAL_STEPS = 4;

const STEP_FOR_FIELD: Record<ReviewEditableField, WizardStep> = {
  firstName: 'firstName',
  lastName: 'firstName', // Redirect lastName edit to firstName step
  companyName: 'companyName',
  frn: 'frn',
};

export default function OnboardingSignupPage() {
  const { token } = useParams<{ token: string }>();
  const [welcomeComplete, setWelcomeComplete] = useState(false);

  const {
    data: invite,
    isLoading,
    error,
  } = useQuery<OnboardingInviteTokenResponse>({
    queryKey: ['public', 'onboarding-invite', token],
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  if (!welcomeComplete) {
    return <WelcomeScreen onComplete={() => setWelcomeComplete(true)} />;
  }

  if (!token) {
    return <InvalidInviteState />;
  }

  if (isLoading) {
    return <AuthPageLayout withHeader isLoading />;
  }

  if (error || !invite) {
    return <InvalidInviteState status={error instanceof ApiError ? error.status : undefined} />;
  }

  return <OnboardingSignupForm email={invite.email} token={token} />;
}

function InvalidInviteState({ status }: { status?: number }) {
  const isExpiredOrUsed = status === 410;

  return (
    <AuthPageLayout withHeader>
      <AuthCard>
        <Stack gap="md" align="center">
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title="This onboarding link cannot be used"
            w="100%"
          >
            {isExpiredOrUsed
              ? 'This link has expired or has already been used to set up an account.'
              : 'This link is invalid.'}
          </Alert>
          <Text size="sm" c="gray.6" ta="center">
            Please contact your Afternoon representative to request a new onboarding link.
          </Text>
        </Stack>
      </AuthCard>
    </AuthPageLayout>
  );
}

// The account exists from 'bookCall' onwards; the stage only decides what is shown
// after that point, never whether the submission happened.
type PostSubmitStage = 'form' | 'bookCallIntro' | 'bookCall' | 'confirmation';

function OnboardingSignupForm({ email, token }: { email: string; token: string }) {
  const [stage, setStage] = useState<PostSubmitStage>('form');
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const [ambiguousTimeout, setAmbiguousTimeout] = useState(false);
  const [step, setStep] = useState<WizardStep>('firstName');
  const [direction, setDirection] = useState<1 | -1>(1);
  const [cameFromReview, setCameFromReview] = useState(false);
  const { submit, isLoading } = useSubmitOnboardingSignup(token);

  const form = useForm<OnboardingSignupFormValues>({
    mode: 'controlled',
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      frn: '',
    },
    validate: {
      firstName: v => (v.trim().length === 0 ? 'First name is required' : null),
      lastName: v => (v.trim().length === 0 ? 'Last name is required' : null),
      companyName: v => (v.trim().length === 0 ? 'Company name is required' : null),
      frn: v => {
        const trimmed = v.trim();
        if (trimmed.length === 0) return 'FCA firm reference number is required';
        return FRN_PATTERN.test(trimmed)
          ? null
          : 'Enter a valid 6 or 7 digit FCA firm reference number';
      },
    },
  });

  // Auto-navigate back to whichever step owns a field error surfaced by a failed submission
  // (see DECISIONS.md's "Submission-error routing" entry) - driven by the form's own error
  // state via effects, not read synchronously after calling `applySubmissionError`, since
  // `form.setFieldError` only takes effect on Mantine's next render.
  useEffect(() => {
    if (form.errors.frn && step !== 'frn') {
      setCameFromReview(false);
      setDirection(-1);
      setStep('frn');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.errors.frn]);

  useEffect(() => {
    if (form.errors.companyName && step !== 'companyName') {
      setCameFromReview(false);
      setDirection(-1);
      setStep('companyName');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.errors.companyName]);

  const goNext = (linearNextStep: WizardStep) => {
    if (cameFromReview) {
      setCameFromReview(false);
      setDirection(1);
      setStep('review');
      return;
    }
    setDirection(1);
    setStep(linearNextStep);
  };

  const goBack = (previousStep: WizardStep) => {
    setCameFromReview(false);
    setDirection(-1);
    setStep(previousStep);
  };

  const editFromReview = (field: ReviewEditableField) => {
    setCameFromReview(true);
    setDirection(-1);
    setStep(STEP_FOR_FIELD[field]);
  };

  const handleSubmit = async () => {
    setTopLevelError(null);
    setAmbiguousTimeout(false);
    const values = form.getValues();
    try {
      await submit({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        companyName: values.companyName.trim(),
        frn: values.frn.trim(),
        contractAgreed: true,
      });
      // Booking is skipped entirely when no Calendly link is configured, so the flow
      // never shows an empty embed.
      setStage(Config.getOnboardingCalendlyUrl() ? 'bookCallIntro' : 'confirmation');
    } catch (submitError) {
      applySubmissionError(submitError, form, setTopLevelError, () => setAmbiguousTimeout(true));
    }
  };

  const calendlyUrl = Config.getOnboardingCalendlyUrl();
  if (stage !== 'form' && stage !== 'confirmation' && calendlyUrl) {
    const values = form.getValues();
    return (
      <AuthPageLayout withHeader={false}>
        <AnimatePresence mode="wait">
          {stage === 'bookCallIntro' ? (
            <BookCallIntroStep key="bookCallIntro" onContinue={() => setStage('bookCall')} />
          ) : (
            <BookCallScreen
              key="bookCall"
              calendlyUrl={calendlyUrl}
              firstName={values.firstName.trim()}
              lastName={values.lastName.trim()}
              email={email}
              onBooked={() => setStage('confirmation')}
              onSkip={() => setStage('confirmation')}
            />
          )}
        </AnimatePresence>
      </AuthPageLayout>
    );
  }

  if (stage === 'confirmation') {
    return <ConfirmationScreen email={email} />;
  }

  const renderStep = () => {
    if (step === 'firstName') {
      return (
        <FirstNameStep
          key="firstName"
          form={form}
          direction={direction}
          currentStepIndex={STEP_INDEX.firstName}
          totalSteps={TOTAL_STEPS}
          onNext={() => goNext('companyName')}
        />
      );
    }
    if (step === 'companyName') {
      return (
        <CompanyNameStep
          key="companyName"
          form={form}
          direction={direction}
          currentStepIndex={STEP_INDEX.companyName}
          totalSteps={TOTAL_STEPS}
          onBack={() => goBack('firstName')}
          onNext={() => goNext('frn')}
        />
      );
    }
    if (step === 'frn') {
      return (
        <FrnStep
          key="frn"
          form={form}
          direction={direction}
          currentStepIndex={STEP_INDEX.frn}
          totalSteps={TOTAL_STEPS}
          onBack={() => goBack('companyName')}
          onNext={() => goNext('review')}
        />
      );
    }
    const values = form.getValues();
    return (
      <ReviewStep
        key="review"
        values={{
          firstName: values.firstName,
          lastName: values.lastName,
          companyName: values.companyName,
          frn: values.frn,
          email,
        }}
        direction={direction}
        currentStepIndex={STEP_INDEX.review}
        totalSteps={TOTAL_STEPS}
        onBack={() => goBack('frn')}
        onSubmit={handleSubmit}
        onEditField={editFromReview}
        isSubmitting={isLoading}
        topLevelError={topLevelError}
        ambiguousTimeout={ambiguousTimeout}
      />
    );
  };

  return (
    <AuthPageLayout withHeader={false}>
      <WizardProgress currentStepIndex={STEP_INDEX[step]} totalSteps={TOTAL_STEPS} />
      <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
    </AuthPageLayout>
  );
}
